import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Price } from "@/components/ui/price";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatNumber } from "@/lib/utils";
import { formatPercent, roundTo } from "../lib/columns";

export interface StatTileProps {
	label: string;
	value: number | null | undefined;
	format?: "money" | "number" | "percent";
	currency?: string;
	icon?: LucideIcon;
	/** Same measure for the previous period; shows "↑ 12%" when given. */
	previous?: number | null;
	/** Whether a rise is good news (sales) or bad news (expenses, returns). */
	goodWhen?: "up" | "down";
	/** Small line under the value. */
	hint?: React.ReactNode;
	/** Emphasis for the measure that matters most on the page. */
	tone?: "default" | "positive" | "negative" | "warning";
	to?: string;
	loading?: boolean;
	className?: string;
}

/** A headline number with its change against the previous period. */
export function StatTile({
	label,
	value,
	format = "number",
	currency,
	icon: Icon,
	previous,
	goodWhen = "up",
	hint,
	tone = "default",
	to,
	loading,
	className,
}: StatTileProps) {
	const { t } = useTranslation();
	const body = (
		<>
			{Icon && (
				<span
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
						tone === "negative"
							? "border-destructive/25 bg-destructive/10 text-destructive"
							: tone === "warning"
								? "border-warning/25 bg-warning/10 text-warning"
								: tone === "positive"
									? "border-success/25 bg-success/10 text-success"
									: "border-primary/20 bg-primary/5 text-primary",
					)}
					aria-hidden="true"
				>
					<Icon className="h-5 w-5" />
				</span>
			)}
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm text-muted-foreground">{label}</p>
				{loading ? (
					<Skeleton className="mt-1.5 h-7 w-28" />
				) : (
					<p
						className={cn(
							"mt-0.5 break-words text-xl font-bold leading-tight tabular-nums",
							tone === "negative" && "text-destructive",
						)}
					>
						<FormattedValue value={value} format={format} currency={currency} />
					</p>
				)}
				{!loading && previous !== undefined && (
					<Delta
						current={value ?? 0}
						previous={previous}
						goodWhen={goodWhen}
						label={t("reports.kpi.vsPrevious")}
					/>
				)}
				{!loading && hint && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{hint}</p>}
			</div>
		</>
	);
	const classes = cn(
		"flex min-w-0 items-start gap-3 rounded-[var(--radius-surface)] border border-border/60 bg-card p-5 shadow-card",
		to &&
			"transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
		className,
	);
	return to ? (
		<Link to={to} className={classes}>
			{body}
		</Link>
	) : (
		<div className={classes}>{body}</div>
	);
}

export function FormattedValue({
	value,
	format,
	currency,
}: {
	value: number | null | undefined;
	format: "money" | "number" | "percent";
	currency?: string;
}) {
	if (value === null || value === undefined || !Number.isFinite(value)) return <>-</>;
	if (format === "money") return <Price value={roundTo(value, 2)} currency={currency} />;
	if (format === "percent") return <span dir="ltr">{formatPercent(value)}</span>;
	return <span dir="ltr">{formatNumber(roundTo(value, 2))}</span>;
}

/** "↑ 12% vs previous period", green when the change is good news, red when it is bad. */
export function Delta({
	current,
	previous,
	goodWhen = "up",
	label,
}: {
	current: number;
	previous: number | null;
	goodWhen?: "up" | "down";
	label: string;
}) {
	const { t } = useTranslation();
	if (previous === null || !Number.isFinite(previous)) return null;
	if (previous === 0) {
		return (
			<p className="mt-1 text-xs text-muted-foreground">
				{current === 0 ? t("reports.kpi.noChange") : t("reports.kpi.noPrevious")}
			</p>
		);
	}
	const change = ((current - previous) / Math.abs(previous)) * 100;
	const flat = Math.abs(change) < 0.05;
	const up = change > 0;
	const good = flat ? null : up === (goodWhen === "up");
	const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
	return (
		<p className="mt-1 flex flex-wrap items-center gap-x-1 text-xs">
			<span
				className={cn(
					"inline-flex items-center gap-0.5 font-semibold",
					good === null ? "text-muted-foreground" : good ? "text-success" : "text-destructive",
				)}
			>
				<Icon className="h-3.5 w-3.5" aria-hidden="true" />
				<span dir="ltr">{formatPercent(Math.abs(change))}</span>
				<span className="sr-only">{flat ? "" : up ? t("reports.kpi.up") : t("reports.kpi.down")}</span>
			</span>
			<span className="text-muted-foreground">{label}</span>
		</p>
	);
}
